import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Column } from '@ant-design/plots';
import { Tabs, Button, Typography, Tag, Flex, Divider, Empty } from '@app/ui';
import * as format from '@app/utils/formatters';

const colors10 = [
    '#1783FF',
    '#00C9C9',
    '#F0884D',
    '#D580FF',
    '#7863FF',
    '#60C42D',
    '#BD8F24',
    '#FF80CA',
    '#009688',
    '#795548',
];

const AudienceCharts = ({ selectedAccount }: any) => {
    const { t } = useTranslation('ownedSocial');
    const [tabKey, setTabKey] = useState('1');
    const [selectedIds, setSelectedIds] = useState<string[]>([]);

    const countryMap = useMemo(
        () => selectedAccount?.insights?.countries || {},
        [selectedAccount?.insights?.countries],
    );
    const cityMap = useMemo(
        () => selectedAccount?.insights?.cities || {},
        [selectedAccount?.insights?.cities],
    );

    // get top 10 stable list based on value
    const topSegments = useMemo(() => {
        const base = tabKey === '1' ? countryMap : cityMap;

        return Object.entries(base)
            .sort(([, aVal], [, bVal]) => Number(bVal) - Number(aVal))
            .slice(0, 10)
            .map(([segmentName, value], index) => ({
                segmentName,
                value,
                color: colors10[index % colors10.length],
            }));
    }, [tabKey, countryMap, cityMap]);

    // init selected state on tab change
    useEffect(() => {
        setSelectedIds(topSegments.map(item => item.segmentName));
    }, [topSegments]);

    // toggle selection
    const toggleItem = (id: string) => {
        setSelectedIds(prev => {
            if (prev.includes(id)) {
                // Prevent if only 2 items are currently selected
                if (prev.length <= 2) return prev;
                return prev.filter(x => x !== id);
            } else {
                return [...prev, id];
            }
        });
    };

    // filter by selected IDs
    const chartData = useMemo(
        () =>
            topSegments.filter(item => selectedIds.includes(item.segmentName)),
        [topSegments, selectedIds],
    );

    const config = useMemo(
        () => ({
            data: chartData,
            xField: 'segmentName',
            yField: 'value',
            colorField: 'segmentName',
            legend: false,
            autoFit: true,
            axis: {
                y: {
                    title: t('audience.followers'),
                    grid: true,
                    line: true,
                    tickLine: true,
                    labelFormatter: value => format.decimal(value),
                    titleFontSize: 16,
                    titleFill: '#5E6066',
                    titleStroke: 'transparent',
                    labelFontSize: 16,
                    labelFill: '#5E6066',
                    labelOpacity: 1,
                },
                x: {
                    labelFontSize: 16,
                    labelFill: '#5E6066',
                    labelOpacity: 1,
                    labelTransform: 'rotate(-30)',
                    labelSpacing: 15,
                },
            },
            style: {
                fill: ({ segmentName }: any) => {
                    const item = topSegments.find(
                        s => s.segmentName === segmentName,
                    );
                    return item?.color || '#ccc';
                },
            },
            tooltip: {
                title: () => t('audience.followers'),
                items: [
                    d => ({
                        name: d.segmentName,
                        value: format.decimal(d.value),
                    }),
                ],
            },
        }),
        [chartData, t],
    );

    return (
        <div className="audience-charts-wrapper">
            <Typography.Title
                level={4}
                style={{ fontFamily: 'pnbold', marginBottom: 0 }}
                className="audience-charts-title"
            >
                {t('audience.charts.topLocationsByFollowers')}
            </Typography.Title>
            <Divider style={{ borderColor: '#0000000f', margin: 0 }} />
            <div className="audience-charts-body">
                <div className="audience-charts-left">
                    <Tabs
                        defaultActiveKey="1"
                        onChange={key => setTabKey(key)}
                        items={[
                            {
                                key: '1',
                                label: t('audience.charts.topCountries'),
                            },
                            { key: '2', label: t('audience.charts.topCities') },
                        ]}
                    />
                    <Flex className="ranker-list" vertical gap={4}>
                        {topSegments?.length ? (
                            topSegments.map((item, index) => {
                                const isSelected = selectedIds.includes(
                                    item.segmentName,
                                );
                                return (
                                    <Button
                                        key={item.segmentName}
                                        style={{
                                            fontFamily: 'pnregular',
                                            justifyContent: 'space-between',
                                            padding: '24px 16px',
                                            borderColor: 'transparent',
                                            borderRadius: 0,
                                            backgroundColor: isSelected
                                                ? 'rgba(186, 224, 255, 0.70)'
                                                : 'transparent',
                                        }}
                                        type={
                                            isSelected ? 'primary' : undefined
                                        }
                                        onClick={() =>
                                            toggleItem(item.segmentName)
                                        }
                                    >
                                        <Flex>
                                            <Typography.Text
                                                style={{
                                                    whiteSpace: 'nowrap',
                                                    marginRight: 16,
                                                    width: 20,
                                                    fontFamily: 'pnregular',
                                                }}
                                            >
                                                {index + 1}.
                                            </Typography.Text>
                                            <Typography.Text
                                                style={{
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    width: 200,
                                                    textAlign: 'left',
                                                    fontFamily: 'pnregular',
                                                }}
                                            >
                                                {item.segmentName}
                                            </Typography.Text>
                                        </Flex>
                                        <Tag
                                            color={item.color}
                                            style={{
                                                width: 60,
                                                textAlign: 'center',
                                                fontFamily: 'pnregular',
                                            }}
                                        >
                                            {format.shortDecimal(item.value, 1)}
                                        </Tag>
                                    </Button>
                                );
                            })
                        ) : (
                            <div
                                className="audience-empty"
                                style={{ minHeight: 130 }}
                            />
                        )}
                    </Flex>
                </div>

                {topSegments?.length ? (
                    <div className="audience-charts-right">
                        <Column {...config} />
                    </div>
                ) : (
                    <div className="audience-empty" style={{ minHeight: 130 }}>
                        <Empty
                            description={t('audience.charts.noLocationData')}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default AudienceCharts;
